import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { authApi } from "@/api/auth";
import { useSession } from "@/stores/session";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/layout/auth-shell";

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmailPage,
});

type State = "verifying" | "ok" | "error";

export function VerifyEmailPage() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [state, setState] = useState<State>(token ? "verifying" : "error");
  // Consume the token exactly once — it's single-use, and React StrictMode
  // mounts effects twice in dev.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current || !token) return;
    ran.current = true;
    authApi
      .verifyEmail(token)
      .then(() => {
        setState("ok");
        // Refresh the session so the verify-email banner clears if they're
        // already logged in.
        void useSession.getState().refresh();
      })
      .catch(() => setState("error"));
  }, [token]);

  if (state === "verifying") {
    return (
      <AuthShell title="Verifying your email" subtitle="One moment…">
        <p className="text-sm text-muted-foreground">Confirming your verification link.</p>
      </AuthShell>
    );
  }

  if (state === "ok") {
    return (
      <AuthShell title="Email verified" subtitle="Your account is fully unlocked.">
        <p className="text-sm text-muted-foreground">
          Thanks — your email address is confirmed. You can now upgrade your plan and use every feature.
        </p>
        <Button asChild className="mt-4 w-full bg-brand text-primary-foreground hover:bg-brand/90">
          <Link to="/">Go to dashboard</Link>
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Verification failed" subtitle="This link didn't work.">
      <p role="alert" className="text-sm text-fail">
        {token
          ? "This verification link is invalid or has expired. Sign in and request a new one from the banner at the top."
          : "This link is missing its verification token. Use the exact link from your email."}
      </p>
      <Button asChild variant="ghost" className="mt-4 w-full">
        <Link to="/login">Back to sign in</Link>
      </Button>
    </AuthShell>
  );
}
